import { Link, useOutletContext, useParams } from 'react-router-dom';
import { Card, Skeleton } from '../../../components/primitives';
import { ChildSubnav } from '../../../components/parent/ChildSubnav';
import type { LinkedChild } from '../../../lib/queries/parent/useLinkedChildren';
import { useChildAiNote } from '../../../lib/queries/parent/useChildAiNote';
import { useParentConsents } from '../../../lib/queries/parent/useParentConsents';
import './AiNote.css';

interface OutletShape {
  activeChildId: string | null;
  children: LinkedChild[];
}

/**
 * `/parent/child/:id/ai-note` — the AI-generated development insight
 * (§08.4) surfaced as a parent-readable note.
 *
 * Two consent gates layered:
 *   1. Server: AI dev analysis only runs for opted-in players (see
 *      /lib/auth/aiOptIn). Non-opted snapshots have no
 *      response_summary written.
 *   2. UI: even when a summary exists, we hide the surface unless the
 *      parent has ticked AI_DEVELOPMENT_ANALYSIS. The legal model is
 *      that the parent is the consent-giver for under-13 children.
 *
 * The view is reading-only — Wave 3.5 lets the parent revoke from
 * Settings → Privacy.
 */
export function ChildAiNote() {
  const { id: routeId } = useParams<{ id: string }>();
  const { children } = useOutletContext<OutletShape>();
  const playerId = routeId ?? null;
  const child = children.find((c) => c.id === playerId) ?? null;

  const consents = useParentConsents();
  const hasAiConsent = consents.data.isActive('AI_DEVELOPMENT_ANALYSIS');

  const { data, loading, error } = useChildAiNote(
    hasAiConsent && playerId ? playerId : null,
  );

  if (!playerId) {
    return (
      <Card variant="opaque">
        <p>Pick a child from the sidebar to see their AI note.</p>
      </Card>
    );
  }

  return (
    <div className="child-ai-note">
      <ChildSubnav childId={playerId} />

      <header className="parent-page__head">
        <span className="parent-page__eyebrow">{child?.teamName ?? 'Linked child'}</span>
        <h1 className="parent-page__title">{(child?.name ?? 'Your child').split(' ')[0]}'s AI note</h1>
        <p className="parent-page__lead">
          A short, plain-English development note written by 4CM's AI from
          your child's recent ratings, goal progress, and wellness logs. Not
          the same as a coach's review — think of it as a gentle pattern
          spotter your child can read for themselves.
        </p>
      </header>

      {consents.loading ? (
        <Card variant="opaque" aria-busy="true">
          <Skeleton variant="text" width="40%" height={16} />
          <Skeleton variant="text" count={2} width="80%" height={14} />
        </Card>
      ) : !hasAiConsent ? (
        <Card variant="opaque">
          <header className="child-ai-note__section-head">
            <span className="parent-page__eyebrow">AI analysis is off</span>
            <h2 className="child-ai-note__section-title">Turn on AI to see this note</h2>
          </header>
          <p className="parent-page__lead">
            You haven't ticked <strong>AI development analysis</strong> in
            your consents. While that's off, 4CM doesn't run the AI
            analysis for {(child?.name ?? 'your child').split(' ')[0]} —
            ratings and coach notes still work as normal.
          </p>
          <p className="parent-page__lead">
            <Link to="/parent/settings">Open Privacy &amp; consent →</Link>
          </p>
        </Card>
      ) : loading ? (
        <Card variant="opaque" aria-busy="true">
          <article className="child-ai-note__body">
            <Skeleton variant="text" count={4} width="100%" height={16} />
          </article>
          <Skeleton variant="text" width="40%" height={12} />
        </Card>
      ) : error ? (
        <Card variant="opaque">
          <p className="parent-page__lead">
            We couldn't load this note right now. Please try again later.
          </p>
        </Card>
      ) : !data?.responseSummary ? (
        <Card variant="opaque">
          <header className="child-ai-note__section-head">
            <span className="parent-page__eyebrow">Nothing yet</span>
            <h2 className="child-ai-note__section-title">No AI note generated yet</h2>
          </header>
          <p className="parent-page__lead">
            4CM generates a fresh note overnight when there's enough new data
            to say something useful. There isn't enough yet — that's
            normal early in a season.
          </p>
        </Card>
      ) : (
        <Card variant="opaque">
          <article className="child-ai-note__body">
            <p className="child-ai-note__paragraph">{data.responseSummary}</p>
          </article>
          {data.snapshotDate && (
            <p className="parent-page__footnote">
              Generated:{' '}
              <strong>
                {new Date(data.snapshotDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </strong>
            </p>
          )}
        </Card>
      )}

      <p className="parent-page__footnote">
        AI notes are advisory. The coach's view, your child's own self-rating,
        and what you see at training all matter more than any AI summary.
      </p>
    </div>
  );
}
